/*
US5006393:
(1) This module allows us to move away from the dependency of this legacy class-based model:
    service_modules/models/all-cards-v2/model.js.
(2) The effort will be made to mimic original fields so to avoid breakage.
(3) Following are the chief benefits for this effort: 
    (a) The above legacy model.js contains an internal method of "refreshData" at the
        each card level which has caused stale data to be returned after the latest adoption
        of a series of "makeGet*" methods (e.g. makeGetApplyNowOnClick).  This effort will allow
        us to restore using these methods again and improve selector efficiency/consistency.
    (b) The above legacy model.js, being a class-based structure, cannot be serialized properly
        (vs. a plain json-based object) for proper SSR output.  This has caused a different
        treatment at different levels before the redux store is created at browser.  This effort
        will basically removes such extra code execusion path and makes the code easier to
        maintain and reason about.  
*/
import { createSelector } from 'reselect';
import {
  getPageData,
  getPageGlobalData,
  getPageCardsData,
  getPageFeatureFlags,
  getExperimentalPageData
} from 'src/universal/redux/page';
import { isBrowser } from 'acq-utils/lib/misc';
import * as crossModelHelper from 'service_modules/models/shared/cross-model-helper';
import { getPageSpecificPhoneObj } from 'service_modules/utils/cross-model-shared';
import pznConfig from 'page_modules/utils/pzn-config';
import { getPznData, hasPznDataArrived } from 'src/universal/redux/pznData.js';
import camelCaseToDash from 'core_modules/utils/camelCaseToDash';

const getStaticData = createSelector(
  [getPageData, getPageGlobalData, getPageCardsData],
  (pageData, globalData, cardList) => {
    const phoneInfo = getPageSpecificPhoneObj(globalData);
    const applyByPhoneVal = {
      ...pageData?.applyByPhone,
      phoneInfo
    };
    const cardArr = Array.isArray(cardList) ? cardList : [];

    const getAppAssets = card => {
      return {
        ...card?.appAssets,
        cardArtDtmTag: pageData?.cardArt?.dtmTag
      };
    };

    const openFaqVal = pageData?.openFAQ
      ? {
          description: pageData.openFAQ.descriptionArray,
          content: (pageData.openFAQ.content || []).map(item => {
            item.header = item.headerBlock;
            if (item.descriptionBlock) {
              item.descriptionBlock.map(block => {
                if (block && !block.description && block.descriptionArray) {
                  block.description = block.descriptionArray;

                  if (Array.isArray(block.descriptionList)) {
                    block.description = block.description.concat(
                      block.descriptionList
                    );
                  }
                }

                if (
                  block &&
                  typeof block.description === 'string' &&
                  Array.isArray(block.descriptionList)
                ) {
                  const updatedList = block.descriptionList.map(item => `- ${item}`);
                  block.description = [block.description].concat(updatedList);
                }
                return block;
              });
              item.description = item.descriptionBlock;
            }
            return item;
          })
        }
      : undefined;

    const benefitTermsContentVal = (pageData?.benefitTerms?.content || []).map(
      item => {
        if (item && !item.description && item.descriptionArray) {
          item.description = item.descriptionArray;
        }
        return item;
      }
    );
    const benefitTermsVal = {
      ...pageData?.benefitTerms,
      content: benefitTermsContentVal
    };

    const { metaData = {} } = { ...pageData.pageInfo };
    Object.keys(metaData).map(key => {
      const newMetaDataKey = camelCaseToDash({ str: key });
      // mutating struct because of keeping original code to avoid breakage.
      pageData.pageInfo.metaData[newMetaDataKey] = metaData[key];
    });

    const result = {
      ...pageData,
      openFAQ: openFaqVal,
      applyByPhone: applyByPhoneVal,
      benefitTerms: benefitTermsVal,
      featuredCards: pageData?.featuredCards || [],
      compareTray: crossModelHelper.getCompareTrayObj(
        pageData.compare,
        pageData,
        globalData?.globalHeaderPage
      ),
      cardTileDtmSectionName: 'Vac',
      cards: cardArr.map(card => {
        const cardFeaturesObj = { ...card.cardFeatures };
        // mutating struct because of keeping original code to avoid breakage.
        if (cardFeaturesObj?.descriptionBlock) {
          const temp = [];
          for (let key in cardFeaturesObj.descriptionBlock) {
            if (Array.isArray(cardFeaturesObj.descriptionBlock[key])) {
              cardFeaturesObj.descriptionBlock[key].map(v => temp.push(v));
            } else {
              temp.push(cardFeaturesObj.descriptionBlock[key]);
            }
          }
          cardFeaturesObj.descriptionList = temp;
        }

        return {
          ...card,
          applyButton: crossModelHelper.getApplyButtonObj(
            card.apply,
            card,
            pznConfig.uniquePageId.categoryPage
          ),
          upgradeButton: crossModelHelper.getUpgradeLink(
            card.requestAnUpgrade || {},
            card
          ),
          features: crossModelHelper.updateFeatures(cardFeaturesObj, card),
          learnMoreButton: crossModelHelper.getLearnMoreLink(card.learnMore, card, {
            pageData
          }),
          offerButtonMeta: crossModelHelper.getOfferButtonMeta(
            card.offerButtonMeta,
            card
          ),
          termLinks: crossModelHelper.getTermLinks(card.tncLinks, card, {
            pageData
          }),
          cardTitle: card.cardHeadline.title,
          interstitialToApp: crossModelHelper.updateInterstitial(card),
          description: card.cardHeadline.header,
          appAssets: getAppAssets(card),
          headerUrls: crossModelHelper.getHeaderUrls(card, {
            pageData: pageData || {}
          }),
          active: !!card.active,
          decommissioned:
            card.decommissioned === undefined ? true : card.decommissioned,
          legalFooter: undefined,
          applyByPhone: applyByPhoneVal,
          vacDtmMap: (pageData?.links || []).reduce((group, item = {}) => {
            if (item.key && item.dtm) {
              group[item.key] = item.dtm;
            }
            return group;
          }, {})
        };
      }),
      'rates&FeesLabel': pageData?.ratesAndFeesLabel
    };
    return result;
  }
);

// this is to combined the static pageData with the dynamic/pzn data.
export const getVacPageData = createSelector(
  [getStaticData, hasPznDataArrived, getPznData, getPageFeatureFlags],
  (pgStaticData, pznArrived, pznData, featureFlags) => {
    if (!isBrowser() || !pznArrived) {
      return pgStaticData;
    }

    /*
    this inner fun mutates incoming object.  adopting it here because:
    (a) the existing/proven code blocks need not to be re-written. 
    (b) a copy was made before this func is called. 
    */
    const processRates = (acmsOffer, cardObj) => {
      if (acmsOffer.penaltyRate) {
        if (!cardObj.penaltyAPR) {
          cardObj.penaltyAPR = { fee: '' };
        }
        cardObj.penaltyAPR.fee = acmsOffer.penaltyRate + cardObj.penaltyAPR.fee;
      }

      if (acmsOffer.rate) {
        cardObj.pznRate = acmsOffer.rate;

        if (!cardObj.aprOnPurchases) {
          cardObj.aprOnPurchases = {};
        }

        cardObj.aprOnPurchases.description = acmsOffer.rate;
      }

      if (acmsOffer.cashAdvanceRate) {
        if (!cardObj.aprOnCashAdvances) {
          cardObj.aprOnCashAdvances = { fee: '' };
        }
        cardObj.aprOnCashAdvances.fee = `${acmsOffer.cashAdvanceRate}${
          cardObj.aprOnCashAdvances.fee
        }`;
      }
    };

    const offerList = Array.isArray(pznData?.pznOfferList)
      ? pznData.pznOfferList
      : [];

    const filteredCardList = (pgStaticData?.cards || []).filter(card => {
      return offerList.find(item => item && item.shortName === card.shortName);
    });

    const updatedCardList = filteredCardList.map(card => {
      const acmsOffer = offerList.find(
        offer => offer.shortName.toLowerCase() === card.shortName.toLowerCase()
      );

      // making a copy first...
      const cardObj = { ...card };

      if (acmsOffer) {
        if (acmsOffer.header || acmsOffer.googleCloudOffer) {
          cardObj.offerContent = {
            googleCloudOffer: acmsOffer.googleCloudOffer
              ? Object.assign({}, acmsOffer.googleCloudOffer)
              : undefined,
            header: acmsOffer.header,
            description: acmsOffer.description,
            content: acmsOffer.content
          };
        }

        cardObj.pznOffer = acmsOffer;
        cardObj.offer.eep = acmsOffer.eep;
        cardObj.offer.pznOfferCode = acmsOffer.pznOfferCode;
        cardObj.offer.defaultOffer = acmsOffer.defaultOffer;

        processRates(acmsOffer, cardObj);

        if (acmsOffer.offerButton) {
          cardObj.offerButton = acmsOffer.offerButton;
        }

        cardObj.order = acmsOffer.order;

        cardObj.termLinks = crossModelHelper.getTermLinks(
          cardObj.tncLinks,
          cardObj,
          { pageData: pgStaticData }
        );

        cardObj.applyButton = crossModelHelper.getApplyButtonObj(
          cardObj.apply,
          cardObj,
          pznConfig.uniquePageId.categoryPage
        );

        cardObj.upgradeButton = crossModelHelper.getUpgradeLink(
          cardObj.requestAnUpgrade || {},
          cardObj
        );

        cardObj.features = crossModelHelper.updateFeatures(
          cardObj.features,
          cardObj
        );

        cardObj.offerButtonMeta = crossModelHelper.getOfferButtonMeta(
          cardObj.offerButtonMeta,
          cardObj
        );

        cardObj.learnMoreButton = crossModelHelper.getLearnMoreLink(
          cardObj.learnMore,
          cardObj,
          { pageData: pgStaticData }
        );

        cardObj.interstitialToApp = crossModelHelper.updateInterstitial(cardObj);

        cardObj.headerUrls = crossModelHelper.getHeaderUrls(cardObj, {
          pageData: pgStaticData
        });
      }

      return cardObj;
    });

    const { useStaticOrderForCardTiles: useStaticOrder } = featureFlags;

    if (!useStaticOrder) {
      // calling a func that causes mutation
      crossModelHelper.orderCardsByOrderProp(updatedCardList);
    }

    const result = {
      ...pgStaticData,
      pznAttributes: pznData?.pznAttributes,
      pznOfferList: offerList,
      cards: updatedCardList
    };
    return result;
  }
);

export const getPropsectFooterLegalCopy = createSelector(
  [getPageData],
  pageData => pageData?.benefitTerms
);

export const getCmFooterLegalCopy = createSelector(
  [getExperimentalPageData],
  pageData => pageData?.cmVACTerms
);
